import { useEffect, useState } from "react";
import styles from "./admin.module.css";
import useSiteSettings from "../../hooks/useSiteSettings";
import { saveSiteSettings } from "../../data/firestore";

const DAY_LABELS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

/** One row per day, so the admin edits hours as data, not free text. */
function hoursToRows(hours) {
  const byDay = {};
  (hours || []).forEach((h) => {
    // expand a combined range like "Monday – Friday" back to each day
    const range = h.day.match(/(\w+)\s*[–—-]\s*(\w+)/);
    if (range) {
      const [a, b] = [DAY_LABELS.indexOf(range[1]), DAY_LABELS.indexOf(range[2])];
      if (a > -1 && b >= a) {
        for (let i = a; i <= b; i++) byDay[DAY_LABELS[i]] = h.time;
        return;
      }
    }
    byDay[h.day] = h.time;
  });
  return DAY_LABELS.map((day) => ({ day, time: byDay[day] || "Closed" }));
}

/** Collapses consecutive identical rows back into "Mon – Fri" ranges
    for compact display on the public site. */
function rowsToHours(rows) {
  const out = [];
  let i = 0;
  while (i < rows.length) {
    let j = i;
    while (j + 1 < rows.length && rows[j + 1].time === rows[i].time) j++;
    out.push({
      day: j > i ? `${rows[i].day} – ${rows[j].day}` : rows[i].day,
      time: rows[i].time,
    });
    i = j + 1;
  }
  return out;
}

export default function SiteSettingsManager() {
  const { doctor, contact, loading } = useSiteSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (loading || form) return;
    setForm({
      email: doctor.email,
      phone: doctor.phone,
      whatsapp: doctor.whatsapp,
      location: doctor.location,
      streetAddress: doctor.address.streetAddress,
      postalCode: doctor.address.postalCode,
      lat: doctor.geo.lat,
      lng: doctor.geo.lng,
      google: doctor.profiles.google,
      practo: doctor.profiles.practo,
      justdial: doctor.profiles.justdial,
      instagram: doctor.profiles.instagram,
      hoursRows: hoursToRows(contact.hours),
      contactIntro: contact.intro,
      contactNote: contact.note,
      credential: doctor.credential,
      role: doctor.role,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (loading || !form) return <p className={styles.hint}>Loading…</p>;

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const setHour = (idx) => (e) =>
    setForm((f) => {
      const rows = [...f.hoursRows];
      rows[idx] = { ...rows[idx], time: e.target.value };
      return { ...f, hoursRows: rows };
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatus("");
    try {
      const digits = form.whatsapp.replace(/\D/g, "");
      await saveSiteSettings({
        email: form.email.trim(),
        phone: form.phone.trim(),
        phoneHref: `+${digits}`,
        whatsapp: digits,
        location: form.location.trim(),
        credential: form.credential.trim(),
        role: form.role.trim(),
        address: {
          streetAddress: form.streetAddress.trim(),
          postalCode: form.postalCode.trim(),
        },
        geo: { lat: form.lat.trim(), lng: form.lng.trim() },
        profiles: {
          google: form.google.trim(),
          practo: form.practo.trim(),
          justdial: form.justdial.trim(),
          instagram: form.instagram.trim(),
        },
        hours: rowsToHours(form.hoursRows),
        contactIntro: form.contactIntro.trim(),
        contactNote: form.contactNote.trim(),
      });
      setStatus("ok");
    } catch {
      setStatus("error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <h3 className={styles.sectionTitle}>Contact</h3>
      <div className={`${styles.formRow} ${styles.formRow2}`}>
        <div className={styles.field}>
          <label htmlFor="s-email">Email</label>
          <input id="s-email" type="email" value={form.email} onChange={set("email")} />
        </div>
        <div className={styles.field}>
          <label htmlFor="s-phone">Phone (display)</label>
          <input id="s-phone" value={form.phone} onChange={set("phone")} placeholder="+91 98765 43210" />
        </div>
      </div>
      <div className={`${styles.formRow} ${styles.formRow2}`}>
        <div className={styles.field}>
          <label htmlFor="s-whatsapp">
            WhatsApp number <span className={styles.hint}>(digits only, country code)</span>
          </label>
          <input id="s-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} placeholder="919876543210" />
        </div>
        <div className={styles.field}>
          <label htmlFor="s-location">Location (short)</label>
          <input id="s-location" value={form.location} onChange={set("location")} />
        </div>
      </div>

      <h3 className={styles.sectionTitle}>Credentials</h3>
      <div className={`${styles.formRow} ${styles.formRow2}`}>
        <div className={styles.field}>
          <label htmlFor="s-credential">Credential letters</label>
          <input id="s-credential" value={form.credential} onChange={set("credential")} placeholder="BPT, MPT" />
        </div>
        <div className={styles.field}>
          <label htmlFor="s-role">Role / title</label>
          <input id="s-role" value={form.role} onChange={set("role")} />
        </div>
      </div>

      <h3 className={styles.sectionTitle}>Clinic address</h3>
      <div className={styles.field}>
        <label htmlFor="s-street">Street address</label>
        <input id="s-street" value={form.streetAddress} onChange={set("streetAddress")} />
      </div>
      <div className={`${styles.formRow} ${styles.formRow3}`}>
        <div className={styles.field}>
          <label htmlFor="s-pin">Pincode</label>
          <input id="s-pin" value={form.postalCode} onChange={set("postalCode")} />
        </div>
        <div className={styles.field}>
          <label htmlFor="s-lat">Latitude</label>
          <input id="s-lat" value={form.lat} onChange={set("lat")} />
        </div>
        <div className={styles.field}>
          <label htmlFor="s-lng">Longitude</label>
          <input id="s-lng" value={form.lng} onChange={set("lng")} />
        </div>
      </div>

      <h3 className={styles.sectionTitle}>Consultation hours</h3>
      <div className={`${styles.formRow} ${styles.formRow3}`}>
        {form.hoursRows.map((row, i) => (
          <div className={styles.field} key={row.day}>
            <label htmlFor={`hr-${row.day}`}>{row.day}</label>
            <input
              id={`hr-${row.day}`}
              value={row.time}
              onChange={setHour(i)}
              placeholder="9:00 — 19:00 or Closed"
            />
          </div>
        ))}
      </div>

      <div className={styles.field}>
        <label htmlFor="s-intro">Contact page intro</label>
        <textarea id="s-intro" rows={2} value={form.contactIntro} onChange={set("contactIntro")} />
      </div>
      <div className={styles.field}>
        <label htmlFor="s-note">Contact page note</label>
        <textarea id="s-note" rows={2} value={form.contactNote} onChange={set("contactNote")} />
      </div>

      <h3 className={styles.sectionTitle}>Listings</h3>
      <div className={`${styles.formRow} ${styles.formRow2}`}>
        <div className={styles.field}>
          <label htmlFor="s-google">Google Business Profile URL</label>
          <input id="s-google" value={form.google} onChange={set("google")} />
        </div>
        <div className={styles.field}>
          <label htmlFor="s-practo">Practo URL</label>
          <input id="s-practo" value={form.practo} onChange={set("practo")} />
        </div>
        <div className={styles.field}>
          <label htmlFor="s-justdial">Justdial URL</label>
          <input id="s-justdial" value={form.justdial} onChange={set("justdial")} />
        </div>
        <div className={styles.field}>
          <label htmlFor="s-instagram">Instagram URL</label>
          <input id="s-instagram" value={form.instagram} onChange={set("instagram")} />
        </div>
      </div>

      <div className={styles.formActions}>
        <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </button>
        {status === "ok" && (
          <span className={`${styles.status} ${styles.statusOk}`}>Saved.</span>
        )}
        {status === "error" && (
          <span className={`${styles.status} ${styles.statusErr}`}>
            Could not save — try again.
          </span>
        )}
      </div>
    </form>
  );
}
